import { Grid, TextField, MenuItem, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { GetState, GetCity } from "react-country-state-city";
import { yupResolver } from "@hookform/resolvers/yup";
import { getRoleAPI } from "../../../../Store/Action/rolePermissionAction";
import { useDispatch } from "react-redux";
import { userSchema } from "../../../../Services/ValidationSchema";
import CustomButton from "../../../../Components/CustomButton";
import Strings from "../../../../Config/Strings";
import Fonts from "../../../../Config/Fonts";
import Colors from "../../../../theme/Colors";

const ManufactureForm = ({ onSubmit }) => {
  const dispatch = useDispatch();
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [stateId, setStateId] = useState(0);
  const [cityId, setCityId] = useState(0);
  const countryId = 101; // India is selected by default
  const [roleList, setRoleList] = useState([]); // State for roles
  const [selectedRoleId, setSelectedRoleId] = useState(""); // State for selected role

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      userEmail: "",
      userMobileNo: "",
      userAddress: "",
      shopName: "",
      state: null,
      city: null,
      role: null, // Add role to form default values
    },
    mode: "onBlur",
  });

  useEffect(() => {
    dispatch(
      getRoleAPI({}, (result) => {
        setRoleList(result);
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (countryId) {
      GetState(countryId).then((result) => {
        setStateList(result);
      });
    }
  }, [countryId]);

  const handleStateChange = useCallback((e) => {
    const selectedStateId = parseInt(e.target.value);
    setStateId(selectedStateId);
    setCityId(0); // Reset city
    GetCity(countryId, selectedStateId).then((result) => {
      setCityList(result);
    });
  }, []);

  const handleCityChange = useCallback((e) => {
    const selectedCityId = parseInt(e.target.value);
    setCityId(selectedCityId);
  }, []);

  const handleRoleChange = useCallback((e) => {
    setSelectedRoleId(e.target.value);
  }, []);

  const renderRequiredAsterisk = useCallback(
    () => <span style={requiredAsteriskStyle}>*</span>,
    []
  );

  const handleFormSubmit = (data) => {
    // Find selected state and city names
    const selectedState =
      stateList.find((state) => state.id === stateId)?.name || "";
    const selectedCity =
      cityList.find((city) => city.id === cityId)?.name || "";

    // Create FormData object
    const formData = new FormData();
    formData.append("user_first_name", data.firstName);
    formData.append("user_last_name", data.lastName);
    formData.append("user_email", data.userEmail);
    formData.append("user_mobile_number", data.userMobileNo);
    formData.append("user_address", data.userAddress);
    formData.append("user_shop_name", data.shopName);

    // Call onSubmit with FormData and separate state and city
    onSubmit({
      formData,
      user_role_id: selectedRoleId,
      user_state: selectedState,
      user_city: selectedCity,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} style={{ padding: "20px" }}>
      <Grid container spacing={2}>
        {/* First Name Field */}
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1" style={inputLabelFontStyle}>
            First Name {renderRequiredAsterisk()}
          </Typography>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="First Name"
                variant="outlined"
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                InputProps={{ style: inputTextStyle }}
              />
            )}
          />
        </Grid>

        {/* Last Name Field */}
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1" style={inputLabelFontStyle}>
            Last Name {renderRequiredAsterisk()}
          </Typography>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Last Name"
                variant="outlined"
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                InputProps={{ style: inputTextStyle }}
              />
            )}
          />
        </Grid>

        {/* Email Field */}
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1" style={inputLabelFontStyle}>
            Email Address {renderRequiredAsterisk()}
          </Typography>
          <Controller
            name="userEmail"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="User Email"
                variant="outlined"
                fullWidth
                error={!!errors.userEmail}
                helperText={errors.userEmail?.message}
                InputProps={{ style: inputTextStyle }}
              />
            )}
          />
        </Grid>

        {/* Phone Number Field */}
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1" style={inputLabelFontStyle}>
            Phone No {renderRequiredAsterisk()}
          </Typography>
          <Controller
            name="userMobileNo"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="User Mobile No"
                variant="outlined"
                fullWidth
                error={!!errors.userMobileNo}
                helperText={errors.userMobileNo?.message}
                InputProps={{ style: inputTextStyle }}
                inputProps={{ maxLength: 10 }}
              />
            )}
          />
        </Grid>

        {/* Address Field */}
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1" style={inputLabelFontStyle}>
            Address {renderRequiredAsterisk()}
          </Typography>
          <Controller
            name="userAddress"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="User Address"
                variant="outlined"
                fullWidth
                error={!!errors.userAddress}
                helperText={errors.userAddress?.message}
                InputProps={{ style: inputTextStyle }}
              />
            )}
          />
        </Grid>

        {/* Shop Name Field */}
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1" style={inputLabelFontStyle}>
            Shop Name {renderRequiredAsterisk()}
          </Typography>
          <Controller
            name="shopName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Shop Name"
                variant="outlined"
                fullWidth
                error={!!errors.shopName}
                helperText={errors.shopName?.message}
                InputProps={{ style: inputTextStyle }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1" style={inputLabelFontStyle}>
            Role 
          </Typography>
          <TextField
            select
            value={selectedRoleId}
            onChange={handleRoleChange}
            fullWidth
            variant="outlined"
            InputProps={{ style: inputTextStyle }}
            error={!!errors.role}
            helperText={errors.role?.message}
          >
            {roleList?.map((item) => (
              <MenuItem
                key={item?.user_role_id}
                value={item?.user_role_id}
              >
                {item?.user_role_name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* State Field */}
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1" style={inputLabelFontStyle}>
            State {renderRequiredAsterisk()}
          </Typography>
          <TextField
            select
            value={stateId}
            onChange={handleStateChange}
            fullWidth
            variant="outlined"
            InputProps={{ style: inputTextStyle }}
            error={!!errors.state}
            helperText={errors.state?.message}
          >
            {stateList.map((item) => (
              <MenuItem key={item?.id} value={item?.id}>
                {item?.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* City Field */}
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1" style={inputLabelFontStyle}>
            City {renderRequiredAsterisk()}
          </Typography>
          <TextField
            select
            value={cityId}
            onChange={handleCityChange}
            fullWidth
            variant="outlined"
            InputProps={{ style: inputTextStyle }}
            error={!!errors.city}
            helperText={errors.city?.message}
          >
            {cityList.map((item) => (
              <MenuItem key={item?.id} value={item?.id}>
                {item?.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <CustomButton
            handleSubmit={handleSubmit}
            isCancelBtn={true}
            label={Strings.Save}
          />
        </Grid>
      </Grid>
    </form>
  );
};

const inputLabelFontStyle = {
  fontSize: 14,
  fontWeight: "400",
  fontFamily: Fonts.NunitoSans,
  color: Colors.black,
  display: "flex",
  justifyContent: "flex-start",
};

const inputTextStyle = {
  fontSize: 14,
  fontWeight: "400",
  fontFamily: Fonts.NunitoSans,
  color: Colors.darkGray,
  backgroundColor: Colors.lightBlue,
  textAlign: 'left',
};

const requiredAsteriskStyle = {
  color: "red",
  marginLeft: "5px",
};

export default ManufactureForm;
